/**
 * Куда ведёт строка панели уведомлений — Блок 2 · Ф7 · В11-a.
 *
 * Отдельного экрана «карточка работы» в приложении нет: работу открывают из
 * списка, в котором она человеку видна. Поэтому цель зависит от ТРЁХ вещей —
 * вида работы, направления события и уровня смотрящего, — и выбирается экран,
 * который (а) этому уровню открыт маршрутизатором и (б) эту работу показывает.
 *
 *   - задача → «Задачи» (и исполнителю, и постановщику);
 *   - работа по объекту, событие «вниз» (получатель — исполнитель) → «Мои
 *     работы», СРАЗУ НА ВКЛАДКЕ «Проектные работы» (В11-f, долг #325: голое
 *     `/my` открывает «Задачи», и работы там нет — адрес был верен, экран по
 *     нему нет); у административного уровня такого экрана нет — карточка
 *     объекта;
 *   - работа по объекту, событие «наверх» (получатель — руководитель) →
 *     карточка объекта;
 *   - событие, ПОСЛЕ которого работы у получателя нет («снята с вас», «работа
 *     снята»), — БЕЗ ссылки: прежний исполнитель в «Моих работах» и в «Задачах»
 *     её уже не найдёт, архивной работы там нет вовсе. Ссылка привела бы на
 *     экран, где искомого нет (ревью cr, da и Codex сошлись на этом).
 *
 * Доска — это не «экран открыт», а «работа на нём ЕСТЬ»: сервер отдаёт рядом
 * со ссылкой три факта о живой работе (`archived`, `viewerIsAssignee`,
 * `viewerIsAuthor`), и по ним видно, что переданной другому работы в «Моих
 * работах» прежнего исполнителя уже нет, а снятой нет ни на одной доске.
 * Руководителю в таком случае остаётся карточка объекта, сотруднику — ничего.
 *
 * `null` — вести некуда: строка остаётся текстом. Ссылка в пустой экран или в
 * «Доступ запрещён» хуже отсутствия ссылки.
 *
 * ⚠️ `item.work === null` ЗНАЧИТ НЕ ТОЛЬКО «РАБОТЫ НЕТ». Сервер отдаёт ссылку
 * лишь по работе, читаемой смотрящим СЕЙЧАС (`WorkService.readableRefs`);
 * человеку, снятому с объекта, строка придёт без ссылки.
 */
import { WorkKind, type NotificationFeedItem, type NotificationFeedKind } from '@aris/shared';

import { myWorkPath } from '@/lib/my-work-link';
import { ROUTE_ACCESS } from '@/lib/route-access';

export type ViewerLevel = 'EMPLOYEE' | 'MANAGER' | 'ADMIN' | 'SUPERVISOR';

/**
 * Направление события: кому оно адресовано относительно работы; `gone` —
 * событие, после которого работа получателю больше не принадлежит.
 *
 * ⚠️ `satisfies Record<NotificationFeedKind, …>` — вид без направления не
 * собирается.
 */
const DIRECTION = {
  WORK_ASSIGNED: 'down',
  TASK_ASSIGNED: 'down',
  WORK_DUE_CHANGED: 'down',
  WORK_CLOSED: 'down',
  WORK_RETURNED: 'down',
  WORK_REOPENED: 'down',
  WORK_DUE_SOON: 'down',
  WORK_IN_PROGRESS: 'up',
  WORK_ON_REVIEW: 'up',
  WORK_UNASSIGNED: 'gone',
  WORK_ARCHIVED: 'gone',
} as const satisfies Record<NotificationFeedKind, 'up' | 'down' | 'gone'>;

/** Открыт ли экран уровню — тем же значением, что читает `routes.tsx`. */
const mayOpen = (screen: keyof typeof ROUTE_ACCESS, level: ViewerLevel): boolean =>
  (ROUTE_ACCESS[screen] as readonly string[]).includes(level);

export function notificationTarget(
  item: Pick<NotificationFeedItem, 'kind' | 'work'>,
  level: ViewerLevel | null,
): string | null {
  const work = item.work;
  if (work === null || level === null) return null;
  const direction = DIRECTION[item.kind];
  if (direction === 'gone') return null;

  // Доски показывают только ЖИВУЮ работу и только тому, чья она: исполнителю —
  // «Мои работы», исполнителю и постановщику — «Задачи». Работа снята или
  // передана другому — на доске её нет, и вести туда незачем.
  if (work.kind === 'TASK') {
    const onBoard = !work.archived && (work.viewerIsAssignee || work.viewerIsAuthor);
    return onBoard && mayOpen('tasks', level) ? '/tasks' : null;
  }

  const projectPath =
    work.projectId !== null && mayOpen('projects', level) ? `/projects/${work.projectId}` : null;

  const inMyWorks = !work.archived && work.viewerIsAssignee;
  // Сюда доходит только работа по объекту (задача ушла выше), поэтому вкладка
  // одна. Вид берётся константой, а не из `work.kind`: появись третий вид, он
  // обязан получить здесь своё решение, а не чужую вкладку по совпадению имён.
  if (direction === 'down' && inMyWorks && mayOpen('myWorks', level)) {
    return myWorkPath(WorkKind.PROJECT_WORK);
  }
  return projectPath;
}
